import React, { useEffect, useState, useRef } from "react";
import { fetchHighResUrl, peekCachedHighResUrl, thumbnailUrl } from "../utils/galleryImageUrls";
import { galleryImageAlt } from "../utils/publicGalleryStrings";
import { getPortfolioItems } from "../utils/portfolioData";
import {
  GalleryImageTile,
  GALLERY_GRID_PRIORITY_COUNT,
} from "./GalleryImageTile";
import "./Portfolio.css";

function imageKey(img, idx) {
  return img.id ?? `idx-${idx}`;
}

// Curated grid role (from the curation pipeline) → grid span class. When absent
// (uncurated / fallback sample) we derive the span from aspect ratio instead.
const ROLE_TO_GRID_CLASS = {
  hero: "grid-hero",
  panorama: "grid-panorama",
  portrait: "grid-vertical",
  standard: "grid-1x1",
};

function preloadImage(url) {
  if (!url) return;
  const img = new Image();
  img.decoding = "async";
  img.src = url;
}

const Portfolio = ({ embedded = false, initialImages = null }) => {
  // Server returns items already ordered (curated order, or its own per-day
  // sample) — render as-is, no client-side shuffle.
  const [images, setImages] = useState(() =>
    Array.isArray(initialImages) ? [...initialImages] : []
  );
  const [loading, setLoading] = useState(!Array.isArray(initialImages));
  const [error, setError] = useState(null);
  const [modalIdx, setModalIdx] = useState(null);
  // Modal opens on the low-res grid thumbnail (already loaded) and upgrades to the true
  // high-res original once it's signed and fetched — no wait before showing *something*.
  const [modalHighResUrl, setModalHighResUrl] = useState(null);
  const [imageAspects, setImageAspects] = useState({});
  const imgRefs = useRef({});

  useEffect(() => {
    if (Array.isArray(initialImages)) {
      setImages([...initialImages]);
      setLoading(false);
      return;
    }

    const fetchImages = async () => {
      setLoading(true);
      try {
        const items = await getPortfolioItems();
        setImages(items);
      } catch (error) {
        console.error("Error fetching portfolio images:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchImages();
  }, [initialImages]);

  // Grid span: prefer the curated role; fall back to aspect ratio.
  const getGridClass = (img, idx) => {
    if (img.gridRole && ROLE_TO_GRID_CLASS[img.gridRole]) {
      return ROLE_TO_GRID_CLASS[img.gridRole];
    }
    const k = imageKey(img, idx);
    if (img.width && img.height) {
      const ratio = img.width / img.height;
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    if (imageAspects[k]) {
      const ratio = imageAspects[k];
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    return "grid-1x1";
  };

  const handleImageLoad = (img, idx) => {
    const k = imageKey(img, idx);
    const imgEl = imgRefs.current[k];
    if (imgEl?.naturalWidth && imgEl.naturalHeight) {
      const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
      setImageAspects((prev) => ({ ...prev, [k]: ratio }));
    }
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (modalIdx === null) return;
      if (e.key === "Escape") setModalIdx(null);
      if (e.key === "ArrowRight") setModalIdx((idx) => (idx + 1) % images.length);
      if (e.key === "ArrowLeft") setModalIdx((idx) => (idx - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalIdx, images.length]);

  useEffect(() => {
    if (modalIdx === null || images.length === 0) return undefined;
    let alive = true;
    // Skip the upgrading-blur flash if this image's high-res URL is already warm in cache
    // (e.g. it was preloaded as a neighbor, or the visitor is reopening a recently viewed photo).
    setModalHighResUrl(peekCachedHighResUrl(images[modalIdx]));
    fetchHighResUrl(images[modalIdx]).then((url) => {
      if (alive) setModalHighResUrl(url);
    });

    const prevIdx = (modalIdx - 1 + images.length) % images.length;
    const nextIdx = (modalIdx + 1) % images.length;
    fetchHighResUrl(images[prevIdx]).then(preloadImage);
    fetchHighResUrl(images[nextIdx]).then(preloadImage);

    return () => {
      alive = false;
    };
  }, [modalIdx, images]);

  return (
    <section className={`portfolio ${embedded ? "portfolio-embedded" : ""}`}>
      {!embedded && <h1 className="portfolio-title">Portfolio</h1>}
      
      {loading && images.length === 0 && (
        <div className="portfolio-loading">Loading portfolio...</div>
      )}
      
      {error && (
        <div className="portfolio-error">
          Failed to load portfolio images. Please try again later.
        </div>
      )}

      <div className="portfolio-grid">
        {images.map((image, idx) => {
          const k = imageKey(image, idx);
          return (
            <GalleryImageTile
              key={k}
              idx={idx}
              className={`portfolio-card ${getGridClass(image, idx)} ${
                loading ? "loading" : ""
              }`}
              onClick={() => setModalIdx(idx)}
              childrenWhenVisible={() => (
                <>
                  {thumbnailUrl(image) ? (
                    <img
                      ref={(el) => {
                        imgRefs.current[k] = el;
                      }}
                      src={thumbnailUrl(image)}
                      alt={image.aiAlt || galleryImageAlt(image)}
                      loading={
                        idx < GALLERY_GRID_PRIORITY_COUNT ? "eager" : "lazy"
                      }
                      decoding="async"
                      fetchPriority={
                        idx < GALLERY_GRID_PRIORITY_COUNT ? "high" : "low"
                      }
                      draggable={false}
                      onLoad={() => handleImageLoad(image, idx)}
                      onError={(e) => {
                        console.error(
                          `Failed to load image: ${thumbnailUrl(image)}`
                        );
                        e.target.src =
                          "https://placehold.co/600x400?text=Image+Not+Found";
                      }}
                    />
                  ) : (
                    <div
                      className="portfolio-missing-url"
                      role="img"
                      aria-label="Missing image URL"
                    />
                  )}
                  {(image.title || image.aiTitle || image.description) && (
                    <div className="card-overlay">
                      {(image.title || image.aiTitle) && (
                        <span className="card-title">
                          {image.title || image.aiTitle}
                        </span>
                      )}
                      {image.description && (
                        <p className="card-description">{image.description}</p>
                      )}
                    </div>
                  )}
                </>
              )}
            />
          );
        })}
      </div>

      {/* Modal for fullsize viewing */}
      {modalIdx !== null && images[modalIdx] && (
        <div
          className="portfolio-modal"
          onClick={() => setModalIdx(null)}
        >
          <button
            className="portfolio-modal-nav portfolio-modal-prev"
            onClick={e => {
              e.stopPropagation();
              setModalIdx((modalIdx - 1 + images.length) % images.length);
            }}
            aria-label="Previous"
          >
            &#8592;
          </button>
          
          <div className="portfolio-modal-content">
            <div className="portfolio-modal-stage">
              <img
                src={modalHighResUrl || thumbnailUrl(images[modalIdx])}
                alt={galleryImageAlt(images[modalIdx])}
                title="Click for next image"
                className={`portfolio-modal-img ${modalHighResUrl ? "" : "is-upgrading"}`}
                fetchPriority="high"
                decoding="async"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalIdx((i) => {
                    if (images.length <= 1) return null;
                    return (i + 1) % images.length;
                  });
                }}
                onError={(e) => {
                  console.error(`Failed to load modal image: ${modalHighResUrl}`);
                  e.target.src = "https://placehold.co/800x600?text=Image+Not+Available";
                }}
              />
            </div>
            {(images[modalIdx].title || images[modalIdx].description) && (
              <div className="portfolio-modal-info">
                {images[modalIdx].title && (
                  <h3 className="portfolio-modal-title">
                    {images[modalIdx].title}
                  </h3>
                )}
                {images[modalIdx].description && (
                  <p className="portfolio-modal-description">
                    {images[modalIdx].description}
                  </p>
                )}
              </div>
            )}
          </div>
          
          <button
            className="portfolio-modal-nav portfolio-modal-next"
            onClick={e => {
              e.stopPropagation();
              setModalIdx((modalIdx + 1) % images.length);
            }}
            aria-label="Next"
          >
            &#8594;
          </button>
        </div>
      )}
    </section>
  );
};

export default Portfolio;